/* eslint-disable react/prop-types */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react'

const ToastContext = createContext()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  const timeoutRefs = useRef({})

  const addToast = useCallback(({ title, message, isError = true, duration = 5000 }) => {
    const id = Date.now() + Math.random()
    const defaultTitle = isError ? 'Error' : 'Success'
    const _title = title || defaultTitle

    const newToast = {
      id,
      title: _title,
      message,
      isError,
      duration,
    }

    setToasts((prev) => [...prev, newToast])

    // Auto remove toast after duration
    if (duration > 0) {
      timeoutRefs.current[id] = setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [])

  const removeToast = useCallback((id) => {
    // Clear timeout if exists
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id])
      delete timeoutRefs.current[id]
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const removeAllToasts = useCallback(() => {
    // Clear all timeouts
    Object.values(timeoutRefs.current).forEach((timeout) => clearTimeout(timeout))
    timeoutRefs.current = {}
    setToasts([])
  }, [])

  // Convenience methods
  const success = useCallback(
    (message, title = 'Success', duration = 5000) => {
      return addToast({ message, title, isError: false, duration })
    },
    [addToast],
  )

  const error = useCallback(
    (message, title = 'Error', duration = 7000) => {
      return addToast({ message, title, isError: true, duration })
    },
    [addToast],
  )

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    success,
    error,
  }

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}
